import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { initializeFirestore, doc, getDocFromServer } from "firebase/firestore";
import firebaseConfigInternal from "@/firebase-applet-config.json";

// Use environment variables if available (for production/Netlify), otherwise fallback to local config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || firebaseConfigInternal.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || firebaseConfigInternal.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || firebaseConfigInternal.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || firebaseConfigInternal.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || firebaseConfigInternal.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || firebaseConfigInternal.appId,
  firestoreDatabaseId: import.meta.env.VITE_FIREBASE_FIRESTORE_DATABASE_ID || firebaseConfigInternal.firestoreDatabaseId,
};

// Debug logging for production (only if config is missing)
if (!firebaseConfig.apiKey) {
  console.warn("Firebase API Key is missing. Make sure to set VITE_FIREBASE_API_KEY in your environment variables.");
}

const app = initializeApp(firebaseConfig);
console.log("Firebase app initialized:", app.name);
export const auth = getAuth(app);

// Initialize Firestore with settings to avoid connection issues in some environments (especially in iframes/proxies)
export const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
}, firebaseConfig.firestoreDatabaseId || "(default)");

console.log("Firestore initialized. Base URL:", `https://console.firebase.google.com/project/${firebaseConfig.projectId}/firestore`);
export const googleProvider = new GoogleAuthProvider();
// Request additional scopes if needed
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Test connection
async function testConnection() {
  try {
    console.log(`Testing Firestore connection to database: ${firebaseConfig.firestoreDatabaseId || "(default)"}...`);
    const testDoc = doc(db, "test", "connection");
    await getDocFromServer(testDoc);
    console.log("Firestore connection successful.");
  } catch (error) {
    console.error("Firestore connection test failed:", error);
    if (error instanceof Error) {
      if (error.message.includes("the client is offline") || error.message.includes("unavailable")) {
        console.error("CRITICAL: Firestore backend is unreachable. This may be due to network restrictions or incorrect configuration.");
        console.log("Tip: If you keep seeing this, try verifying if the Firestore database ID is correct in firebase-applet-config.json.");
      }
      if (error.message.includes("not found") || error.message.includes("does not exist")) {
        console.error(`CRITICAL: Database ${firebaseConfig.firestoreDatabaseId} might not exist. Check your Firestore instances.`);
      }
    }
  }
}
testConnection();

export enum OperationType {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  LIST = "list",
  GET = "get",
  WRITE = "write",
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  const errInfo: FirestoreErrorInfo = {
    error: errorMessage,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  };

  console.error("Firestore Error: ", JSON.stringify(errInfo));

  // If it's a permission error, throw the raw JSON string as requested by the protocol
  if (errorMessage.toLowerCase().includes("permission") || errorMessage.toLowerCase().includes("insufficient")) {
    throw JSON.stringify(errInfo);
  }
  
  throw new Error(JSON.stringify(errInfo));
}
